import { FieldPolicy, Reference } from '@apollo/client';
import { QueueItemsPagination } from '../models/graphql';

// this will store the latest filter key globally within the scope of the merge policy.
// we use this to reference the returned (incoming) set to make sure its still the intended results.
export const latestFilterKey: { key: string | undefined } = { key: undefined };

// Helper function to safely extract the ID
function getIdFromItem(readField: (fieldName: string, obj: Reference | any) => any, item: Reference | any) {
  if (!item) return undefined;
  if ('__ref' in item) {
    return readField('id', item); // Apollo Reference
  }
  return item.id; // Plain object
}

export function mergePostSchedule(
  existing: QueueItemsPagination | undefined,
  incoming: QueueItemsPagination,
  currentFilterKey: string | undefined,
  readField: (fieldName: string, obj: Reference | any) => any
): QueueItemsPagination | undefined {
  // discard outdated responses before updating the cache
  if (latestFilterKey.key && currentFilterKey && currentFilterKey !== latestFilterKey.key) {
    return existing;
  }

  const existingCursorItems = existing?.cursorPagination?.items || [];
  const incomingCursorItems = incoming?.cursorPagination?.items || [];

  const existingDateRangeItems = existing?.dateRangePagination?.items || [];
  const incomingDateRangeItems = incoming?.dateRangePagination?.items || [];

  // Merge and deduplicate `cursorPagination` items
  const mergedCursorItems = [
    ...existingCursorItems,
    ...incomingCursorItems.filter(incomingItem => {
      const incomingId = getIdFromItem(readField, incomingItem);
      return !existingCursorItems.some(existingItem => {
        const existingId = getIdFromItem(readField, existingItem);
        return existingId === incomingId;
      });
    })
  ];

  // Merge and deduplicate `dateRangePagination` items
  const mergedDateRangeItems = [
    ...existingDateRangeItems,
    ...incomingDateRangeItems.filter(incomingItem => {
      const incomingId = getIdFromItem(readField, incomingItem);
      return !existingDateRangeItems.some(existingItem => {
        const existingId = getIdFromItem(readField, existingItem);
        return existingId === incomingId;
      });
    })
  ];

  return {
    __typename: 'QueueItemsPagination',
    cursorPagination: {
      ...incoming.cursorPagination,
      items: mergedCursorItems,
      count: mergedCursorItems.length,
      cursor: incoming.cursorPagination?.cursor ?? undefined
    },
    dateRangePagination: {
      ...incoming.dateRangePagination,
      items: mergedDateRangeItems,
      count: mergedDateRangeItems.length,
      startDate: incoming.dateRangePagination?.startDate ?? undefined,
      endDate: incoming.dateRangePagination?.endDate ?? undefined
    }
  };
}

export const postScheduleMergePolicy = (): FieldPolicy => {
  return {
    keyArgs: false,
    merge(existing: QueueItemsPagination | undefined, incoming: QueueItemsPagination, { readField, variables }) {
      return mergePostSchedule(existing, incoming, variables?.filterKey, readField);
    }
  };
};
